import React from 'react';
import styled from 'styled-components';

import Badge from 'kingpin/atoms/Badge';
import Typography from 'kingpin/atoms/Typography';
import Row from 'components/Common/Row';

// Validation, in two clearly different registers: an error means publishing
// breaks the runtime or strands content; a warning means the answer may be
// wrong but nothing falls over. The token meter sits here too — the always-on
// block is what every single answer pays for.

const PanelDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const IssueDiv = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 8px 12px;
  border-radius: 6px;
  background-color: var(--color-bg-secondary);
`;

const MeterTrack = styled.div`
  height: 6px;
  border-radius: 3px;
  background-color: var(--color-bg-tertiary);
  overflow: hidden;
  width: 240px;
`;

const MeterFill = styled.div<{ ratio: number; isOver: boolean }>`
  height: 100%;
  width: ${(props) => Math.min(props.ratio, 1) * 100}%;
  background-color: ${(props) =>
    props.isOver
      ? 'var(--color-status-danger)'
      : 'var(--color-status-success)'};
`;

const ValidationPanel = ({
  validation,
}: {
  validation: AgentKb.ValidationResult | null;
}) => {
  if (!validation) {
    return null;
  }
  const isOver = validation.alwaysOnTokens > validation.tokenBudget;
  return (
    <PanelDiv data-testid="agent-kb-validation">
      <Row centerAlign style={{ gap: 12 }}>
        <Typography.Body type="Label">Always-on block</Typography.Body>
        <MeterTrack>
          <MeterFill
            ratio={validation.alwaysOnTokens / validation.tokenBudget}
            isOver={isOver}
          />
        </MeterTrack>
        <Typography.Body type="Body 12" color="var(--color-text-secondary)">
          ~{validation.alwaysOnTokens.toLocaleString()} of{' '}
          {validation.tokenBudget.toLocaleString()} tokens, every answer
        </Typography.Body>
      </Row>

      {validation.errors.length === 0 && validation.warnings.length === 0 && (
        <Typography.Body type="Body 13" color="var(--color-text-secondary)">
          No issues. This draft is publishable.
        </Typography.Body>
      )}

      {[...validation.errors, ...validation.warnings].map((issue, index) => (
        <IssueDiv key={`${issue.code}-${index}`}>
          <Badge
            badgeType={issue.level === 'error' ? 'Red' : 'Orange'}
            text={issue.level === 'error' ? 'Blocks publish' : 'Advisory'}
          />
          <Typography.Body type="Body 13">
            {issue.artefactId ? `${issue.artefactId}: ` : ''}
            {issue.message}
          </Typography.Body>
        </IssueDiv>
      ))}
    </PanelDiv>
  );
};

export default ValidationPanel;
